import React, { useState, useEffect } from 'react';
import Modal from '../../common/Modal';
import { XMarkIcon, DocumentIcon } from '@heroicons/react/24/outline';
import { supabase } from '../../../../lib/supabase';
import { useProject } from '../../../../context/ProjectContext';
import EditableTitle from '../../common/EditableTitle';

export default function GetDocumentSettingsModal({ 
  isOpen, 
  onClose, 
  data, 
  onSave,
  nodeName 
}) {
  const { currentProject } = useProject();
  const [settings, setSettings] = useState({
    label: data.label || nodeName,
    selectedDocument: data.selectedDocument || null,
    type: 'getdocument',
    category: 'document'
  });

  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [showDocumentSelector, setShowDocumentSelector] = useState(false);

  const filteredDocuments = documents.filter(doc => 
    doc.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  useEffect(() => {
    if (isOpen) {
      loadDocuments();
    }
  }, [isOpen, currentProject?.id]);

  const loadDocuments = async () => {
    if (!currentProject?.id) return;

    try {
      const { data: docs, error } = await supabase
        .from('data_docs')
        .select('*')
        .eq('project_id', currentProject.id)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setDocuments(docs || []);
    } catch (error) {
      console.error('Error loading documents:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    const nodeData = {
      label: settings.label,
      type: 'getdocument',
      category: 'document',
      selectedDocument: settings.selectedDocument ? {
        id: settings.selectedDocument.id,
        name: settings.selectedDocument.name
      } : null
    };

    onSave(nodeData);
    onClose();
  };

  const handleDelete = () => {
    if (!deleteConfirm) {
      setDeleteConfirm(true);
      setTimeout(() => setDeleteConfirm(false), 3000);
      return;
    }
    data.onDelete(data.id);
    onClose();
  };

  const handleAddDocument = (doc) => {
    setSettings(prev => ({
      ...prev,
      selectedDocument: doc
    }));
  };

  const handleChangeDocument = () => {
    setSearchTerm('');
    setSettings(prev => ({
      ...prev,
      selectedDocument: null
    }));
  };

  if (!isOpen) return null;

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <div className="w-[800px] max-w-[90vw] max-h-[90vh] bg-dark-200 rounded-lg flex flex-col">
        {/* Header */}
        <div className="p-6 border-b border-dark-400">
          <div className="flex items-center justify-between">
            <EditableTitle 
              title={settings.label || nodeName || 'Get Document'} 
              onSave={(newTitle) => {
                setSettings(prev => ({
                  ...prev,
                  label: newTitle
                }));
              }}
              className="text-xl font-semibold text-gray-900 dark:text-white"
            />
            <button onClick={handleClose} className="text-gray-400 hover:text-gray-300">
              <XMarkIcon className="w-6 h-6" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 p-6 overflow-y-auto">
          {/* Document Selection */}
          <div className="space-y-6">
            <div>
              <label className="block text-sm font-medium mb-4">Selected Document</label>
              {settings.selectedDocument ? (
                <div className="flex items-center justify-between p-3 bg-dark-300 border border-dark-400 rounded">
                  <div className="flex items-center gap-3">
                    <DocumentIcon className="w-5 h-5 text-purple-400" />
                    <span>{settings.selectedDocument.name}</span>
                  </div>
                  <button
                    onClick={handleChangeDocument}
                    className="text-sm text-accent-primary hover:text-accent-primary/90"
                  >
                    Change
                  </button>
                </div>
              ) : (
                <div>
                  {/* Search Input */}
                  <input
                    type="text"
                    placeholder="Search documents..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full px-4 py-2 mb-4 bg-dark-300 border border-dark-400 rounded-lg focus:outline-none focus:ring-2 focus:ring-accent-primary"
                  />

                  {/* Document List */}
                  <div className="space-y-2 max-h-[400px] overflow-y-auto">
                    {loading ? (
                      <div className="text-center py-4 text-gray-400">Loading...</div>
                    ) : filteredDocuments.length === 0 ? (
                      <div className="text-center py-4 text-gray-400">
                        No documents found
                      </div>
                    ) : (
                      filteredDocuments.map(doc => (
                        <button
                          key={doc.id}
                          onClick={() => handleAddDocument(doc)}
                          className="w-full flex items-center gap-3 p-3 rounded-lg transition-colors bg-dark-300 hover:bg-dark-400 border border-transparent"
                        >
                          <DocumentIcon className="w-5 h-5 text-purple-400" />
                          <div className="flex-1 text-left">
                            <div className="font-medium">{doc.name}</div>
                            <div className="text-sm text-gray-400">Document</div>
                          </div>
                        </button>
                      ))
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="border-t border-dark-400 p-4">
          <button
            type="button"
            onClick={handleDelete}
            className={`flex items-center gap-1 text-sm transition-colors ${
              deleteConfirm 
                ? 'bg-red-500 hover:bg-red-600 text-white px-3 py-1 rounded' 
                : 'text-gray-400 hover:text-red-500'
            }`}
          >
            <XMarkIcon className="w-3.5 h-3.5" />
            <span>{deleteConfirm ? 'Click to confirm delete' : 'Delete node'}</span>
          </button>
        </div>
      </div>
    </Modal>
  );
} 